import React from 'react';
import styled from '@emotion/styled';
import { colors, mediaQueries } from '../variables';
import Cart from '../img/cart.svg';

// ─── 資料層 ─────────────────────────────────────────────────────────────────
// getAllTierPrices 回傳值的型別說明：
// Array<{
//   v_key: string,
//   variation_label: string,   // 包裝規格名（中文）
//   weight: number,            // 公斤數，用於排序與量價階梯比較
//   tier_prices: Array<{
//     tier_id, tier_label, tier_order,
//     avg_price: number|null,  // 每公斤均價（整數，/kg 單位移至欄頭）
//     dollars: number|null     // 該規格總價
//   }>
// }>

const Product = (props) => {
  const {
    imageUrl,
    titleZh,
    titleEng,
    product_price_tiers,
    memberTier,
    desc,
    // version: 1 試算表型 / 2 緊湊卡型 / 3 清單行型
    version = 1,
    processing,
    // beanIndex：App 層傳入的「整支豆」序號，用於斑馬以豆為單位交替上色
    beanIndex = 0,
  } = props;

  // 整支豆斑馬：偶數豆 = 白底，奇數豆 = 斑馬底（同支豆所有 variation 列共用同底色）
  const is_zebra_bean = beanIndex % 2 === 1;

  // membership_tiers API → id 對照表（支援陣列或 {items:[...]}）
  const tier_list = Array.isArray(memberTier) ? memberTier : (memberTier?.items || []);
  const tier_info_map = {};
  tier_list.forEach((t, idx) => {
    if (!t?.id) return;
    tier_info_map[t.id] = {
      name: t.name_translations?.['zh-hant'] || t.name || t.id,
      order: typeof t.level === 'number' ? t.level : idx,
    };
  });

  // 依 variation 分組，過濾有庫存，按 weight 升序
  const getAllTierPrices = () => {
    if (!product_price_tiers || product_price_tiers.length === 0) return [];
    const keys_with_stock = product_price_tiers
      .filter(item => item?.variation_details?.quantity > 0)
      .map(item => item.variation_key);
    const unique_keys = [...new Set(keys_with_stock)];
    if (unique_keys.length === 0) return [];

    return unique_keys.map(v_key => {
      const items = product_price_tiers.filter(
        item => item.variation_key === v_key && item?.variation_details?.quantity > 0
      );
      const first = items[0];
      const tier_prices = items.map(item => {
        const info = tier_info_map[item.membership_tier_id];
        return {
          tier_id: item.membership_tier_id,
          tier_label: info?.name || item.member_price?.label || item.membership_tier_id,
          tier_order: info?.order ?? 999,
          avg_price: item.member_price?.avg_price ?? null,
          dollars: item.member_price?.dollars ?? null,
        };
      }).sort((a, b) => a.tier_order - b.tier_order)
        // 銀卡會員資訊不顯示：僅保留 銅卡 + 金卡（最低 + 最高等級）
        .filter(tp => !String(tp.tier_label).includes('銀'));

      return {
        v_key,
        variation_label: first?.variation_details?.fields_translations?.['zh-hant'] || v_key,
        weight: first?.variation_details?.weight || 0,
        tier_prices,
      };
    }).sort((a, b) => a.weight - b.weight);
  };

  const all_tier_price_groups = getAllTierPrices();

  // ── 行銷要素計算 ──────────────────────────────────────────────────────────
  // 升金卡省額：最低等級 avg_price − 最高等級 avg_price，> 0 才回傳
  const getTierSaving = (group) => {
    const priced = group.tier_prices.filter(tp => tp.avg_price != null);
    if (priced.length < 2) return 0;
    const saving = priced[0].avg_price - priced[priced.length - 1].avg_price;
    return saving > 0 ? saving : 0;
  };

  // 最高等級（金卡）tier
  const getTopTier = (group) => {
    const priced = group.tier_prices.filter(tp => tp.avg_price != null);
    return priced.length ? priced[priced.length - 1] : null;
  };

  // 數字格式：整數 toLocaleString（資料已為整數，不需小數位）
  const fmtPrice = (n) => (n != null ? Math.round(n).toLocaleString() : '—');

  // 規格名去除開頭重量與外層括號（重量改由前置標籤呈現，避免「4kg 4kg(真空包裝)」重複）
  // 例：「4kg(真空包裝)」→「真空包裝」、「16kg(盒裝，4kg*4包)」→「盒裝，4kg*4包」、「4kg」→「」
  const stripLeadingWeight = (label) =>
    String(label)
      .replace(/^\s*\d+(?:\.\d+)?\s*kg\s*/i, '')
      .replace(/^[（(]\s*/, '')
      .replace(/\s*[)）]\s*$/, '')
      .trim();

  // 下單：用 Shopline Open API 的 express link（經 server proxy，token 不進前端）把商品
  // 直接掛進購物車，開啟回傳的 /cart 連結。已登入會員→直達購物車且商品已加好；
  // 未登入→落在登入頁，登入後商品已在車內。失敗則退回原本「開商品頁」行為。
  // variationId：指定要加入的包裝規格（每個規格列各自帶入自己的 v_key）；
  //   未指定時退回「最小包裝規格」（all_tier_price_groups 已按重量升冪，[0] 為最小）。
  const openPurchase = async (variationId) => {
    const productId = props.props?.id;
    if (!variationId) variationId = all_tier_price_groups[0]?.v_key;
    const fallbackUrl = `${process.env.REACT_APP_API_URL}${props.props.link}`;
    // 先在使用者手勢內開好空白分頁，避免 await 後 window.open 被彈窗攔截
    const win = window.open('', '_blank');
    if (!productId || !variationId) {
      if (win) win.location = fallbackUrl;
      return;
    }
    try {
      const resp = await fetch('/api/v1/merchants/generate_express_link', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: [{ id: productId, variation_id: variationId }] }),
      });
      const data = await resp.json();
      if (win) win.location = data?.link || fallbackUrl;
    } catch (e) {
      if (win) win.location = fallbackUrl;
    }
  };

  // =========================================================================
  // V1 — 試算表型
  //   改動：/kg 移至欄頭、金卡欄 goldTint 底 + primary 左線、
  //         斑馬以「整支豆」為單位、字重收斂、row-hover、print 色保留
  // =========================================================================
  const renderV1 = () => {
    const tier_headers = all_tier_price_groups[0]?.tier_prices || [];
    // 判斷哪個欄位 index 是金卡（最高等級）
    const top_tier_idx = tier_headers.length > 0 ? tier_headers.length - 1 : -1;
    return (
      <V1Wrapper>
        <div className="product-head">
          <div className="thumb">
            {imageUrl && <img src={imageUrl} alt={titleZh} />}
          </div>
          <div className="name-block">
            <span className="zh">{titleZh}</span>
            <span className="eng">{titleEng}</span>
            {processing && <span className="proc">· {processing}</span>}
            {desc && <span className="desc">{desc}</span>}
          </div>
        </div>

        {all_tier_price_groups.length === 0
          ? <div className="no-price">無價格資料</div>
          : (
            <table className={`spec-table${is_zebra_bean ? ' alt-bean' : ''}`}>
              {/* colgroup：固定欄寬，三個會員等級欄嚴格等寬 */}
              <colgroup>
                <col className="cg-spec" />
                {tier_headers.map((tp, i) => (
                  <col className="cg-tier" key={`cg-${tp.tier_id || i}`} />
                ))}
                <col className="cg-saving" />
                <col className="cg-buy" />
              </colgroup>
              <thead>
                <tr>
                  <th className="col-spec">規格</th>
                  {tier_headers.map((tp, i) => (
                    <th
                      className={`col-tier${i === top_tier_idx ? ' gold-head' : ''}`}
                      key={`th-${tp.tier_id || i}`}
                    >
                      {/* /kg 移至欄頭，以「等級 NT$/kg」呈現 */}
                      {tp.tier_label}<small> NT$/kg</small>
                    </th>
                  ))}
                  <th className="col-saving">升金卡省/kg</th>
                  <th className="col-buy">下單</th>
                </tr>
              </thead>
              <tbody>
                {all_tier_price_groups.map((group, row_idx) => {
                  const saving = getTierSaving(group);
                  const top_tier = getTopTier(group);
                  const is_last = row_idx === all_tier_price_groups.length - 1;
                  // 重量標籤移到前面；後面只留包裝描述（去掉重複的開頭重量）
                  const pkg_label = group.weight > 0
                    ? stripLeadingWeight(group.variation_label)
                    : group.variation_label;
                  return (
                    <tr key={group.v_key} className={is_last ? 'last-row' : ''}>
                      <td className="col-spec">
                        {group.weight > 0 && (
                          <span className="weight-tag">{group.weight}kg</span>
                        )}
                        {pkg_label && <span className="spec-name">{pkg_label}</span>}
                      </td>
                      {group.tier_prices.map((tp, idx) => {
                        const is_top = top_tier && tp.tier_id === top_tier.tier_id;
                        return (
                          <td
                            className={`col-tier${is_top ? ' gold' : ''}`}
                            /* data-tier：手機卡片式重排時，由 td::before 帶出欄頭等級名 */
                            data-tier={tp.tier_label}
                            key={`${group.v_key}-${tp.tier_id || idx}`}
                          >
                            {/* 純數字，/kg 已在欄頭；兩等級價格統一黑色 */}
                            {tp.avg_price != null
                              ? <span className={is_top ? 'num-gold' : 'num'}>{fmtPrice(tp.avg_price)}</span>
                              : <span className="dash">—</span>}
                          </td>
                        );
                      })}
                      <td className="col-saving">
                        {saving > 0
                          ? <span className="saving-val">NT${saving}</span>
                          : <span className="dash">—</span>}
                      </td>
                      <td className="col-buy">
                        {/* 每個規格列各自下單：加入「該規格 1 件」到購物車 */}
                        <button
                          type="button"
                          className="row-buy"
                          title={`將 ${group.weight > 0 ? group.weight + 'kg ' : ''}${pkg_label || ''} 加入購物車`}
                          onClick={() => openPurchase(group.v_key)}
                        >
                          <img src={Cart} alt="下單" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </V1Wrapper>
    );
  };

  // =========================================================================
  // V2 — 緊湊卡網格型
  //   改動：金卡 price-cell.gold 加 goldTint 底 + primary 左線、字重收斂、
  //         spec-row hover、欄頭文案統一
  // =========================================================================
  const renderV2 = () => (
    <V2Wrapper>
      <div className="card-top">
        {imageUrl && <img className="thumb" src={imageUrl} alt={titleZh} />}
        <div className="name-block">
          <div className="zh">{titleZh}</div>
          <div className="eng">{titleEng}</div>
          {processing && <div className="proc">{processing}</div>}
        </div>
      </div>

      {all_tier_price_groups.length === 0
        ? <div className="no-price">無價格資料</div>
        : (
          <div className="price-zone">
            {all_tier_price_groups[0]?.tier_prices && (
              <div className="tier-head">
                <span className="lbl">規格</span>
                {all_tier_price_groups[0].tier_prices.map((tp, i) => {
                  const is_top = i === all_tier_price_groups[0].tier_prices.length - 1;
                  return (
                    <span
                      className={`lbl-tier${is_top ? ' gold-head' : ''}`}
                      key={`head-${tp.tier_id || i}`}
                    >
                      {tp.tier_label.replace('會員', '')}
                    </span>
                  );
                })}
                {/* 欄頭文案：空間緊，用「升金省」 */}
                <span className="lbl lbl-save">升金省</span>
                <span className="lbl lbl-buy">下單</span>
              </div>
            )}
            {all_tier_price_groups.map((group) => {
              const saving = getTierSaving(group);
              const top_tier = getTopTier(group);
              return (
                <div className="spec-row" key={group.v_key}>
                  <span className="spec-lbl">{group.variation_label}</span>
                  {group.tier_prices.map((tp, idx) => {
                    const is_top = top_tier && tp.tier_id === top_tier.tier_id;
                    return (
                      <span
                        className={`price-cell${is_top ? ' gold' : ''}`}
                        key={`${group.v_key}-${tp.tier_id || idx}`}
                      >
                        {tp.avg_price != null ? fmtPrice(tp.avg_price) : '—'}
                      </span>
                    );
                  })}
                  <span className="save-cell">
                    {saving > 0 ? <b className="orange">{saving}</b> : '—'}
                  </span>
                  {/* 每個規格列各自下單：加入「該規格 1 件」到購物車 */}
                  <span className="buy-cell">
                    <button
                      type="button"
                      className="row-buy"
                      title={`將 ${group.variation_label} 加入購物車`}
                      onClick={() => openPurchase(group.v_key)}
                    >
                      <img src={Cart} alt="下單" />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}

      {desc && <div className="desc">{desc}</div>}
    </V2Wrapper>
  );

  // =========================================================================
  // V3 — 清單行型
  //   改動：/kg 移至欄頭、金卡 data-cell.gold 加 goldTint 底 + primary 左線、
  //         字重收斂、tier-data-row hover、欄頭文案統一
  // =========================================================================
  const renderV3 = () => (
    <V3Wrapper $zebra={is_zebra_bean}>
      <div className="left-col">
        {imageUrl && <img className="thumb" src={imageUrl} alt={titleZh} />}
        <div className="name-block">
          <span className="zh">{titleZh}</span>
          <span className="eng">{titleEng}</span>
          {processing && <span className="proc">· {processing}</span>}
          {desc && <span className="desc">{desc}</span>}
        </div>
      </div>

      <div className="price-col">
        {all_tier_price_groups.length === 0
          ? <span className="no-price">無價格資料</span>
          : all_tier_price_groups.map((group, g_idx) => {
              const saving = getTierSaving(group);
              const top_tier = getTopTier(group);
              const is_first = g_idx === 0;
              return (
                <div className="variation-block" key={group.v_key}>
                  {is_first && (
                    <div className="tier-head-row">
                      <span className="spec-head">規格</span>
                      {group.tier_prices.map((tp, i) => {
                        const is_top = i === group.tier_prices.length - 1;
                        return (
                          <span
                            className={`tier-head-cell${is_top ? ' gold-head' : ''}`}
                            key={`head-${tp.tier_id || i}`}
                          >
                            {/* /kg 收斂到欄頭：「金卡 NT$/kg」 */}
                            {tp.tier_label.replace('會員', '')} NT$/kg
                          </span>
                        );
                      })}
                      <span className="saving-head">升金卡省/kg</span>
                    </div>
                  )}
                  <div className="tier-data-row">
                    <span className="spec-cell">{group.variation_label}</span>
                    {group.tier_prices.map((tp, idx) => {
                      const is_top = top_tier && tp.tier_id === top_tier.tier_id;
                      return (
                        <span
                          className={`data-cell${is_top ? ' gold' : ''}`}
                          key={`${group.v_key}-${tp.tier_id || idx}`}
                        >
                          {/* 純數字，/kg 已在欄頭 */}
                          {tp.avg_price != null ? fmtPrice(tp.avg_price) : '—'}
                        </span>
                      );
                    })}
                    <span className="saving-cell">
                      {saving > 0
                        ? <span className="orange">NT${saving}</span>
                        : '—'}
                    </span>
                  </div>
                </div>
              );
            })}
      </div>

      <button type="button" className="buy-btn-inline" onClick={openPurchase}>
        <img src={Cart} alt="下單" />
      </button>
    </V3Wrapper>
  );

  if (version === 2) return renderV2();
  if (version === 3) return renderV3();
  return renderV1();
};

export default Product;

// ─── Styled Components ───────────────────────────────────────────────────────
// 共用：金卡欄 print 色彩強制保留（黑白 PDF 仍可見底色）
const printGoldRule = `
  @media print {
    .gold, .gold-head, .weight-tag {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

/* ── V1 試算表型 ── */
const V1Wrapper = styled.div`
  width: 100%;
  border: 1px solid ${colors.lightGray};
  border-radius: 6px;
  overflow: hidden;            /* 圓角裁切內部表格邊緣 */
  background: ${colors.white};
  box-sizing: border-box;
  /* 商品分層：淡陰影 + 卡間距，讓每支豆成為獨立卡片、好區隔 */
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 10px;
  font-variant-numeric: tabular-nums;
  ${printGoldRule}

  .product-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 10px 12px;
    background: ${colors.gray};
    border-bottom: 1px solid ${colors.lightGray};
    .thumb { flex: 0 0 136px; img { width: 136px; height: 136px; object-fit: cover; display: block; border-radius: 4px; } }
    .name-block {
      order: 1; flex: 1; text-align: left;
      /* 品名：中文強制獨立一行，英文+處理法落到下一行，避免中英擠成一行難讀 */
      .zh { display: block; font-size: 16px; font-weight: 600; color: ${colors.primary}; margin-bottom: 2px; }
      .eng { font-size: 16px; color: ${colors.muted}; margin-right: 6px; }
      .proc { font-size: 14px; color: ${colors.cta}; }
      /* 風味描述：完整揭露（不截斷），自動換行，列印/PDF 亦完整 */
      .desc {
        display: block; font-size: 16px; color: ${colors.muted}; line-height: 1.55;
        white-space: normal; overflow-wrap: break-word; word-break: break-word; max-width: 100%;
      }
    }
  }

  .no-price { font-size: 14px; color: ${colors.muted}; padding: 8px 12px; text-align: left; }

  .spec-table {
    width: 100%; border-collapse: collapse;
    /* fixed layout + colgroup：三個會員欄嚴格等寬，金卡不再吃掉多餘空間 */
    table-layout: fixed;
    /* 只剩 銅卡 + 金卡 兩個等級欄；最右新增「下單」窄欄（每規格各自加入購物車） */
    col.cg-spec { width: 28%; }
    col.cg-tier { width: 20%; }
    col.cg-saving { width: 20%; }
    col.cg-buy { width: 12%; }
    th, td { padding: 6px 12px; font-size: 14px; border-bottom: 1px solid ${colors.lightGray}; }

    /* 欄頭：底線用 box-shadow 補（border-collapse 下一般 border 易被覆蓋） */
    thead th {
      background: ${colors.gray};
      color: ${colors.primary};
      /* 字重降到 600，欄頭不搶焦點 */
      font-weight: 600;
      font-size: 14px;
      text-align: right;
      box-shadow: inset 0 -1px 0 ${colors.lightGray};
      small { font-size: 14px; color: ${colors.muted}; font-weight: 400; margin-left: 2px; }
    }
    thead th.col-spec { text-align: left; }

    /* 金卡欄頭：分隔線統一為表格邊框色（lightGray 1px），不再深綠粗線 */
    thead th.gold-head {
      border-left: 1px solid ${colors.lightGray};
    }

    .col-spec { text-align: left; white-space: nowrap; }
    /* 兩個價格欄置中：等寬欄置中後，欄界的綠色分隔線剛好落在兩個數字正中間，較順眼 */
    .col-tier { text-align: center; white-space: nowrap; }
    .col-saving { text-align: right; white-space: nowrap; }
    .col-buy { text-align: center; white-space: nowrap; }
    thead th.col-buy { text-align: center; }

    /* 每規格下單鈕：綠色購物車膠囊，點擊把該規格 1 件加入購物車 */
    .row-buy {
      display: inline-flex; align-items: center; justify-content: center;
      background: ${colors.cta}; border: none; border-radius: 4px; cursor: pointer;
      padding: 5px 10px;
      img { height: 15px; display: block; }
      &:hover { background: ${colors.primary}; }
    }

    /* 規格名：500 常規，不再 600 */
    .spec-name { font-weight: 500; }
    /* 重量標籤：移到最前面，改實心綠色膠囊（白字粗體），一眼掃出量價階梯 */
    .weight-tag {
      display: inline-block; vertical-align: baseline;
      margin-right: 8px; font-size: 14px; font-weight: 700;
      color: ${colors.white}; background: ${colors.cta};
      border-radius: 3px; padding: 1px 7px; letter-spacing: 0.3px;
    }

    /* 兩等級會員價格統一黑色（移除銅/金 shimmer 漸層）；金卡略大略粗作為目標級層次 */
    .num {
      font-weight: 500; font-size: 14px;
      color: ${colors.black};
    }
    .num-gold {
      font-weight: 700; font-size: 15px;
      color: ${colors.black};
    }

    /* 金卡 td：分隔線統一為表格邊框色（lightGray 1px） */
    td.gold {
      border-left: 1px solid ${colors.lightGray};
    }

    /* 省額：唯二的橘色粗體焦點 */
    .saving-val { color: ${colors.orange}; font-weight: 700; font-size: 14px; }
    .dash { color: ${colors.lightGray}; }
    tbody tr.last-row td { border-bottom: none; }

    /* 斑馬色「以整支豆為單位」：每支豆是獨立 V1Wrapper / 獨立 table，
       由 App 層傳入的 productIndex 決定整支豆套白底或綠灰底（.alt-bean）。
       同一支豆的所有 variation 規格列因此共用同一底色，相鄰豆才換色，
       避免把 A 豆 16kg 誤讀成 B 豆 4kg。 */
    tbody tr { background: ${colors.white}; }

    /* row-hover：壓過斑馬 */
    tbody tr {
      transition: background-color 90ms linear;
      &:hover { background: #eef3f0 !important; }
    }
  }

  /* 整支豆斑馬：奇數序的豆整支套綠灰底（同豆所有列同色） */
  .spec-table.alt-bean tbody tr { background: ${colors.zebra}; }

  /* ── 手機版（<=768px）：5 欄表格在窄寬會橫向溢出，改「每規格一張卡」 ──
     table/thead/tbody/tr/td 全部 display:block 重排：
     - 表頭 thead 隱藏（欄頭文字改用 td::before 行內標籤帶出）
     - 每個 tr（一個規格）= 一張小卡：第一列規格名，第二列三等級價格等寬並排，省額另起一行
     - 三等級仍並排可比、金卡仍 goldTint 底 + 左線 + 粗體
     - 無橫向捲動，375/390px 不破版 */
  ${mediaQueries.mobile} {
    /* 手機 product-head：改「文字環繞圖片+按鈕」消除圖片下/按鈕下的空白
       - 取消 desktop 的 flex 三欄；圖片 float 左、下單 float 右
       - 品名+風味描述自然繞排，超過圖片/按鈕高度後佔滿整寬
       - 圖片縮到 100px 把版面留給文字；風味描述放大好讀 */
    .product-head {
      display: block;
      padding: 10px 12px;
      &::after { content: ''; display: block; clear: both; }

      .thumb {
        float: left;
        margin: 0 10px 4px 0;
        img { width: 100px; height: 100px; }
      }
      .name-block {
        display: block;
        .zh { font-size: 16px; line-height: 1.35; }
        .eng { font-size: 16px; }
        .proc { font-size: 14px; }
        .desc { font-size: 16px; line-height: 1.65; margin-top: 5px; }
      }
    }

    .spec-table {
      display: block;
      table-layout: auto;

      /* colgroup 在 block 重排下無作用，隱藏避免影響 */
      colgroup { display: none; }
      thead { display: none; }
      tbody { display: block; }

      /* 每個規格 = 一張卡 */
      tbody tr,
      &.alt-bean tbody tr {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-areas:
          'spec   spec'
          'bronze gold'
          'saving buy';
        align-items: end;
        gap: 2px 8px;
        padding: 8px 10px 10px;
        border-bottom: 1px solid ${colors.lightGray};
        background: inherit;
      }
      tbody tr.last-row { border-bottom: none; }

      th, td { padding: 0; border-bottom: none; font-size: 14px; }

      /* 規格名列：橫跨整寬 */
      td.col-spec {
        grid-area: spec;
        text-align: left; white-space: normal;
        display: flex; align-items: baseline; gap: 6px;
        padding-bottom: 4px; margin-bottom: 2px;
        border-bottom: 1px dashed ${colors.divider};
      }
      .spec-name { font-weight: 600; font-size: 15px; }
      /* 手機 col-spec 為 flex，間距交給 gap，膠囊不再額外加右邊距；字級略放大 */
      .weight-tag { margin-right: 0; font-size: 14px; padding: 1px 8px; }

      /* 三等級價格欄：等寬並排，欄頭以 ::before 帶出 */
      td.col-tier {
        text-align: right; white-space: nowrap;
        display: flex; flex-direction: column; align-items: stretch;
        padding: 3px 4px;
      }
      td.col-tier::before {
        content: attr(data-tier);
        display: block; font-size: 14px; color: ${colors.muted};
        font-weight: 400; text-align: right; margin-bottom: 1px;
      }
      /* col-spec 是第 1 個 td，移除銀卡後價格欄為第 2(銅)/3(金) 個 td */
      td.col-tier:nth-of-type(2) { grid-area: bronze; }
      td.col-tier:nth-of-type(3) { grid-area: gold; }

      /* 金卡欄：分隔線統一為表格邊框色（lightGray 1px） */
      td.col-tier.gold {
        border-left: 1px solid ${colors.lightGray};
        border-radius: 0 2px 2px 0;
        padding-left: 8px;
      }
      /* 手機 2 欄重排下金卡左鄰是 bronze(nth-of-type 2)，右側留白不貼綠線 */
      td.col-tier:nth-of-type(2) { padding-right: 8px; }
      td.col-tier.gold::before { color: ${colors.primary}; font-weight: 600; }
      .num { font-size: 15px; }
      .num-gold { font-size: 16px; }

      /* 省額：整寬靠左帶完整文案（手機有空間，不縮成代號） */
      td.col-saving {
        grid-area: saving; text-align: left;
        padding-top: 4px; margin-top: 2px;
        border-top: 1px dashed ${colors.divider};
      }
      td.col-saving::before {
        content: '升金卡每公斤省 ';
        font-size: 14px; color: ${colors.muted}; font-weight: 400;
      }
      .saving-val { font-size: 14px; }

      /* 下單：與省額同一列、靠右下；按鈕加大為好按的觸控目標 */
      td.col-buy {
        grid-area: buy;
        text-align: right; justify-self: end; align-self: end;
        padding-top: 4px; margin-top: 2px;
        border-top: 1px dashed ${colors.divider};
      }
      .row-buy { padding: 6px 14px; img { height: 17px; } }

      /* hover 在觸控裝置無意義，但保留不影響；金卡底色已由 .gold 維持 */
    }
  }
`;

/* ── V2 緊湊卡網格型 ── */
const V2Wrapper = styled.div`
  width: 100%;
  background: ${colors.white};
  border: 1px solid ${colors.lightGray};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 8px 10px 6px;
  gap: 6px;
  font-variant-numeric: tabular-nums;
  text-align: left;
  ${printGoldRule}

  /* 縮圖放大到與 V1 list 相同（136px）：grid 卡片的圖不該比清單還小。
     card-top 固定高度 = 縮圖高度（136px），只放圖 + 品名（品名最多約 4 行放得下）
     → 下方價格表在各卡同一水平線開始（跨卡對齊）；風味移到價格表下方完整顯示、不截斷。 */
  .card-top {
    display: flex; align-items: flex-start; gap: 10px;
    height: 136px;
    .thumb { width: 136px; height: 136px; object-fit: cover; flex-shrink: 0; display: block; border-radius: 4px; }
    .name-block {
      flex: 1; min-width: 0; text-align: left; overflow: hidden;
      /* 字級對齊 V1：品名 16 / 英文 12 / 處理法 12 */
      .zh { font-size: 16px; font-weight: 600; color: ${colors.primary}; line-height: 1.3; }
      .eng { font-size: 16px; color: ${colors.muted}; }
      .proc { font-size: 14px; color: ${colors.cta}; }
    }
  }

  /* 風味描述：移到價格表下方完整揭露（不截斷），字級對齊 V1 desc(10px)；上方細線分隔 */
  .desc {
    margin-top: 8px; padding-top: 8px;
    border-top: 1px solid ${colors.divider};
    font-size: 16px; color: ${colors.muted}; line-height: 1.55; text-align: left;
    white-space: normal; overflow-wrap: break-word; word-break: break-word;
  }

  .no-price { font-size: 14px; color: ${colors.muted}; }

  /* 字級全面對齊 V1：表頭 12 / 規格 13 / 銅卡價 14 / 金卡價 15 / 省額 13 */
  .price-zone {
    display: flex; flex-direction: column; gap: 3px;
    font-size: 14px;

    .tier-head {
      display: flex; align-items: center; gap: 4px;
      border-bottom: 1px solid ${colors.lightGray}; padding-bottom: 3px; margin-bottom: 3px;
      .lbl { font-size: 14px; color: ${colors.muted}; flex: 1.4; font-weight: 400; }
      .lbl-save { flex: 0.8; text-align: right; }
      .lbl-buy { flex: 0.7; text-align: center; }
      .lbl-tier {
        font-size: 14px; color: ${colors.muted}; flex: 1;
        text-align: right; font-weight: 400;
      }
      /* 金卡欄頭：分隔線統一表格邊框色（與 V1 一致，不再金底） */
      .lbl-tier.gold-head {
        border-left: 1px solid ${colors.lightGray};
        color: ${colors.primary};
        font-weight: 600;
        padding-left: 4px;
      }
    }

    /* 量價階梯：每 variation 一行 */
    .spec-row {
      display: flex; align-items: baseline; gap: 4px;
      padding: 3px 0;
      border-bottom: 1px solid ${colors.divider};
      transition: background-color 90ms linear;
      &:last-of-type { border-bottom: none; }
      &:hover { background: ${colors.rowHover}; }

      .spec-lbl { flex: 1.4; font-size: 14px; color: ${colors.black}; font-weight: 500; }

      /* 兩等級價格統一黑色（與 V1 一致）：銅卡 14 / 金卡 15 加粗 */
      .price-cell {
        flex: 1; text-align: right; font-size: 14px;
        color: ${colors.black}; font-weight: 500;
      }
      .price-cell.gold {
        font-size: 15px; font-weight: 700;
        border-left: 1px solid ${colors.lightGray};
        padding-left: 4px;
      }

      .save-cell { flex: 0.8; text-align: right; font-size: 14px; color: ${colors.muted}; }
      .orange { color: ${colors.orange}; font-weight: 700; }

      /* 每規格下單：靠右綠色購物車鈕，加入該規格 1 件 */
      .buy-cell { flex: 0.7; display: flex; align-items: center; justify-content: center; align-self: center; }
      .row-buy {
        display: inline-flex; align-items: center; justify-content: center;
        background: ${colors.cta}; border: none; border-radius: 4px; cursor: pointer;
        padding: 4px 8px;
        img { height: 14px; display: block; }
        &:hover { background: ${colors.primary}; }
      }
    }
  }

  /* ── 手機版（<=768px）：V2 改「一排兩張」直式緊湊卡（與 V1 單欄清單明顯區隔）──
     圖在上滿版方形、品名在下、價格表壓縮、下單滿寬釘底；窄欄字級縮小避免破版 */
  ${mediaQueries.mobile} {
    padding: 6px;

    .card-top {
      flex-direction: column;
      align-items: stretch;
      height: auto;
      gap: 6px;
      .thumb { width: 100%; height: auto; aspect-ratio: 1 / 1; border-radius: 4px; }
      .name-block {
        overflow: visible;
        .zh { font-size: 14px; line-height: 1.3; }
        .eng { font-size: 16px; }
        .proc { font-size: 14px; }
      }
    }

    /* 風味描述（價格表下方）：文字大小與顏色同步 V1 手機（13px / line-height 1.65 / muted） */
    .desc { margin-top: 6px; padding-top: 6px; font-size: 16px; line-height: 1.65; }

    .price-zone {
      font-size: 14px; gap: 2px;
      .tier-head {
        gap: 2px; padding-bottom: 2px; margin-bottom: 2px;
        .lbl { font-size: 14px; flex: 1.1; }
        .lbl-tier { font-size: 14px; }
        .lbl-save { font-size: 14px; flex: 0.8; }
        .lbl-tier.gold-head { padding-left: 2px; }
      }
      .spec-row {
        gap: 2px; padding: 2px 0; align-items: baseline;
        .spec-lbl { font-size: 14px; flex: 1.1; line-height: 1.25; white-space: normal; overflow-wrap: break-word; }
        .price-cell { font-size: 14px; }
        .price-cell.gold { font-size: 14px; padding-left: 2px; }
        .save-cell { font-size: 14px; flex: 0.8; }
      }
    }

    /* 保留 base 的 margin-top:auto（釘底），僅覆寫滿寬置中與內距；
       勿再設 margin-top，否則會蓋掉 auto 導致按鈕貼在風味描述下方、各卡高低不一 */
    .buy-link {
      align-self: stretch; justify-content: center;
      padding: 6px 0;
    }
  }
`;

/* ── V3 清單行型 ── */
const V3Wrapper = styled.div`
  width: 100%;
  /* 整支豆斑馬：每支豆是一個橫條，整條交替底色（白 / 綠灰），相鄰豆才換色 */
  background: ${props => (props.$zebra ? colors.zebra : colors.white)};
  border-bottom: 1px solid ${colors.lightGray};
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 8px 12px;
  font-variant-numeric: tabular-nums;
  ${printGoldRule}

  .left-col {
    display: flex; align-items: flex-start; gap: 8px; flex: 0 0 260px;
    ${mediaQueries.tablet} { flex: 0 0 200px; }
    ${mediaQueries.mobile} { flex: 0 0 auto; max-width: 160px; }
    .thumb { width: 36px; height: 36px; object-fit: cover; flex-shrink: 0; display: block; }
    .name-block {
      flex: 1; text-align: left; overflow: hidden;
      .zh { display: block; font-size: 14px; font-weight: 600; color: ${colors.primary}; }
      .eng { display: block; font-size: 16px; color: ${colors.muted}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .proc { display: block; font-size: 14px; color: ${colors.cta}; }
      /* 風味描述：完整揭露（不截斷），自動換行，列印/PDF 亦完整 */
      .desc {
        display: block; font-size: 16px; color: ${colors.muted}; line-height: 1.55; margin-top: 2px;
        overflow-wrap: break-word; word-break: break-word;
      }
    }
  }

  .price-col {
    flex: 1; display: flex; flex-direction: column; gap: 0;

    /* 欄頭：/kg 收斂到欄頭（「金卡 NT$/kg」） */
    .tier-head-row {
      display: flex; align-items: center;
      border-bottom: 2px solid ${colors.lightGray};
      padding-bottom: 3px; margin-bottom: 3px;
      .spec-head {
        flex: 1.5; font-size: 14px; color: ${colors.muted}; font-weight: 400;
      }
      .tier-head-cell {
        flex: 1; text-align: right; font-size: 14px;
        color: ${colors.muted}; font-weight: 400;
      }
      /* 金卡欄頭：goldTint 底 + 左線 */
      .tier-head-cell.gold-head {
        background: ${colors.goldTint};
        border-left: 2px solid ${colors.primary};
        color: ${colors.primary}; font-weight: 600;
        padding-left: 3px;
      }
      .saving-head {
        flex: 0.9; text-align: right; font-size: 14px;
        color: ${colors.muted}; font-weight: 400;
      }
    }

    /* 量價階梯：每 variation 一行 */
    .variation-block { display: flex; flex-direction: column; }
    .tier-data-row {
      display: flex; align-items: baseline;
      padding: 3px 0;
      border-bottom: 1px solid ${colors.divider};
      transition: background-color 90ms linear;
      &:last-of-type { border-bottom: none; }
      &:hover { background: ${colors.rowHover}; }
      &:hover .data-cell.gold { background: ${colors.rowHoverGold}; }

      /* 規格名：500 */
      .spec-cell { flex: 1.5; font-size: 14px; color: ${colors.black}; font-weight: 500; }

      /* 銅銀卡：400 黑；金卡：goldTint 底 + 左線 + 700 primary */
      .data-cell {
        flex: 1; text-align: right; font-size: 14px;
        color: ${colors.black}; font-weight: 400;
      }
      .data-cell.gold {
        color: ${colors.primary}; font-weight: 700;
        background: ${colors.goldTint};
        border-left: 2px solid ${colors.primary};
        padding-left: 3px;
      }

      .saving-cell { flex: 0.9; text-align: right; font-size: 14px; color: ${colors.muted}; }
      .orange { color: ${colors.orange}; font-weight: 700; }
    }
  }

  .buy-btn-inline {
    flex-shrink: 0; background: ${colors.cta}; border: none; border-radius: 3px;
    cursor: pointer; padding: 5px 8px; align-self: center;
    img { width: 14px; height: 14px; display: block; }
  }

  /* ── 手機版（<=768px）：左右並排太擠，改上下堆疊 ──
     - 整個 wrapper 改 column：上排品名+縮圖+風味，中排價格表佔整寬，下排下單鈕整寬
     - 價格表三等級維持等寬清楚、金卡底色+左線+粗體保留、風味描述完整不截斷 */
  ${mediaQueries.mobile} {
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
    padding: 8px 12px 10px;

    .left-col {
      flex: 1 1 auto;
      max-width: none;
      align-items: flex-start;
      .name-block {
        overflow: visible;
        /* 手機品名可完整顯示，英文小標解除單行截斷 */
        .eng { white-space: normal; overflow: visible; text-overflow: clip; }
      }
    }

    .price-col {
      flex: 1 1 auto;
      width: 100%;
      .tier-head-row {
        .spec-head { flex: 1.3; }
        .tier-head-cell { font-size: 14px; }
        .saving-head { flex: 1; font-size: 14px; }
      }
      .tier-data-row {
        padding: 4px 0;
        .spec-cell { flex: 1.3; font-size: 14px; }
        .data-cell { font-size: 14px; }
        .saving-cell { flex: 1; font-size: 14px; }
      }
    }

    .buy-btn-inline {
      align-self: stretch;
      padding: 8px;
      display: flex; align-items: center; justify-content: center; gap: 6px;
      /* 整寬下單鈕：加文字提升點擊辨識與觸控面積 */
      &::after {
        content: '下單';
        color: ${colors.white}; font-size: 14px; font-weight: 700;
      }
    }
  }
`;
